export interface PostalCodeLookupResult {
  state: string;
  city: string;
  colonias: string[];
  lat: number;
  lng: number;
}

interface NominatimAddress {
  city?: string;
  town?: string;
  municipality?: string;
  county?: string;
  village?: string;
  state?: string;
}

interface NominatimResult {
  lat: string;
  lon: string;
  address?: NominatimAddress;
}

function extractCity(address: NominatimAddress): string | null {
  return (
    address.city ??
    address.town ??
    address.municipality ??
    address.county ??
    address.village ??
    null
  );
}

async function lookupFromNominatim(
  zip: string,
): Promise<{ state: string; city: string; lat: number; lng: number } | null> {
  const params = new URLSearchParams({
    postalcode: zip,
    country: "Mexico",
    format: "json",
    addressdetails: "1",
    limit: "1",
  });

  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?${params.toString()}`,
    {
      headers: {
        Accept: "application/json",
        "Accept-Language": "es",
      },
    },
  );

  if (!response.ok) {
    return null;
  }

  const results: NominatimResult[] = await response.json();
  const match = results[0];
  if (!match?.address) {
    return null;
  }

  const city = extractCity(match.address);
  const state = match.address.state;
  if (!city || !state) {
    return null;
  }

  return {
    city,
    state,
    lat: parseFloat(match.lat),
    lng: parseFloat(match.lon),
  };
}

async function lookupColoniasFromZippopotam(zip: string): Promise<string[]> {
  try {
    const response = await fetch(`https://api.zippopotam.us/mx/${zip}`);
    if (!response.ok) {
      return [];
    }

    const data: {
      places?: { "place name": string }[];
    } = await response.json();

    const names = (data.places ?? [])
      .map((place) => place["place name"]?.trim())
      .filter((name): name is string => Boolean(name));

    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, "es"));
  } catch {
    return [];
  }
}

export async function lookupMexicanPostalCode(
  zip: string,
): Promise<PostalCodeLookupResult | null> {
  const normalized = zip.trim();
  if (!/^\d{5}$/.test(normalized)) {
    return null;
  }

  try {
    const [location, colonias] = await Promise.all([
      lookupFromNominatim(normalized),
      lookupColoniasFromZippopotam(normalized),
    ]);

    if (!location) {
      return null;
    }

    return {
      ...location,
      colonias,
    };
  } catch {
    return null;
  }
}
